import React from 'react';
import { AlertTriangle, Loader2, X } from 'lucide-react';

const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title = "Confirm Action", 
  message = "Are you sure you want to perform this action?", 
  confirmText = "Confirm", 
  cancelText = "Cancel", 
  variant = "danger", // 'danger' | 'warning' | 'info' | 'success'
  type, // Alias for variant
  isLoading = false,
  loading, // Alias for isLoading
  showCancel = true
}) => {
  if (!isOpen) return null;

  // Handle aliases
  const activeVariant = type || variant;
  const activeLoading = loading !== undefined ? loading : isLoading;

  const variantStyles = {
    danger: {
      iconBg: 'bg-red-50',
      iconColor: 'text-red-500',
      buttonBg: 'bg-red-600 hover:bg-red-700 shadow-red-200'
    },
    warning: {
      iconBg: 'bg-yellow-50',
      iconColor: 'text-yellow-500',
      buttonBg: 'bg-yellow-600 hover:bg-yellow-700 shadow-yellow-200'
    },
    info: {
      iconBg: '', 
      iconColor: '',
      iconStyle: { backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', color: 'var(--primary-color, #4f46e5)' },
      buttonStyle: { backgroundColor: 'var(--primary-color, #4f46e5)' }
    },
    success: {
      iconBg: 'bg-green-50',
      iconColor: 'text-green-500',
      buttonBg: 'bg-green-600 hover:bg-green-700 shadow-green-200'
    }
  };

  const style = variantStyles[activeVariant] || variantStyles.danger;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in duration-300">
        <div className="flex justify-end p-4 absolute right-0 top-0">
          <button 
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 text-center">
          <div 
            className={`w-16 h-16 ${style.iconBg || ''} rounded-full flex items-center justify-center mx-auto mb-4`}
            style={style.iconStyle}
          >
            <AlertTriangle className={`w-8 h-8 ${style.iconColor || ''}`} />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-500 text-sm leading-relaxed">
            {message}
          </p>
        </div>

        <div className="flex items-center gap-3 p-4 bg-gray-50 mt-2">
          {showCancel && (
            <button
              type="button"
              disabled={activeLoading}
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm font-bold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-50"
            >
              {cancelText}
            </button>
          )}
          <button
            type="button"
            disabled={activeLoading}
            onClick={onConfirm || onClose}
            className={`flex-1 px-4 py-2.5 text-sm font-bold text-white ${style.buttonBg || ''} rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70`}
            style={style.buttonStyle}
          >
            {activeLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
